import React, { useRef, useCallback } from 'react';
import { css } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../../styles';
import { useAutoRotate } from './useAutoRotate';
import { FEATURES, TIMING } from './constants';
import { AIChatAnimation } from './animations/AIChatAnimation';
import { SmartAlertsAnimation } from './animations/SmartAlertsAnimation';
import { PortfolioAnimation } from './animations/PortfolioAnimation';
import { NewsOnChartsAnimation } from './animations/NewsOnChartsAnimation';
import { DailyPicksAnimation } from './animations/DailyPicksAnimation';
import { MarketUpdatesAnimation } from './animations/MarketUpdatesAnimation';

interface FeatureShowcaseProps {
  className?: string;
}

const ANIMATION_COMPONENTS = [
  AIChatAnimation,
  DailyPicksAnimation,
  NewsOnChartsAnimation,
  SmartAlertsAnimation,
  PortfolioAnimation,
  MarketUpdatesAnimation,
] as const;

export const FeatureShowcase: React.FC<FeatureShowcaseProps> = ({ className }) => {
  const { currentIndex, progress, pause, resume, goTo, next, previous } = useAutoRotate({
    totalItems: FEATURES.length,
    intervalMs: TIMING.autoRotateInterval,
  });

  // Touch handling for swipe
  const touchStartX = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      pause();
    },
    [pause],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current !== null) {
        const deltaX = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(deltaX) > TIMING.swipeThreshold) {
          if (deltaX > 0) {
            previous();
          } else {
            next();
          }
        }
        touchStartX.current = null;
      }
      resume();
    },
    [next, previous, resume],
  );

  const handleMouseEnter = useCallback(() => {
    pause();
  }, [pause]);

  const handleMouseLeave = useCallback(() => {
    resume();
  }, [resume]);

  const currentFeature = FEATURES[currentIndex];

  return (
    <div
      ref={containerRef}
      className={className}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      css={css`
        display: flex;
        flex-direction: column;
        width: 100%;
      `}
    >
      {/* Animation container with integrated title */}
      <div
        css={css`
          position: relative;
          height: 290px;
          overflow: hidden;
          border-radius: 20px;
          background: ${colors.sugar};
          box-shadow:
            0 1px 3px rgba(0, 0, 0, 0.04),
            0 4px 12px rgba(0, 0, 0, 0.06);

          html[data-theme='dark'] & {
            background: ${colors.obsidian};
            box-shadow:
              0 1px 3px rgba(0, 0, 0, 0.2),
              0 4px 12px rgba(0, 0, 0, 0.3);
          }
        `}
      >
        {/* Title overlay at top */}
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            z-index: 10;
            padding: ${spacing.deci} ${spacing.deci} ${spacing.centi};
          `}
        >
          {/* Description as main text - prominent value proposition */}
          <div
            css={css`
              ${fontStyles.lion};
              color: ${colors.liquorice};
              font-weight: 500;

              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {currentFeature.description}
          </div>
        </div>

        {/* Animation area */}
        <div
          css={css`
            position: absolute;
            top: 56px;
            left: 0;
            right: 0;
            bottom: 0;
          `}
        >
          {ANIMATION_COMPONENTS.map((Component, index) => (
            <div
              key={FEATURES[index].id}
              css={css`
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                opacity: ${index === currentIndex ? 1 : 0};
                transform: ${index === currentIndex ? 'translateX(0)' : 'translateX(8px)'};
                transition:
                  opacity ${TIMING.transitionDuration}ms ease-out,
                  transform ${TIMING.transitionDuration}ms ease-out;
                pointer-events: ${index === currentIndex ? 'auto' : 'none'};
              `}
            >
              <Component isActive={index === currentIndex} />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation icons with progress */}
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          gap: ${spacing.deci};
          margin-top: ${spacing.deci};
        `}
      >
        {FEATURES.map((feature, index) => {
          const isActive = index === currentIndex;

          return (
            <button
              key={feature.id}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`View ${feature.title}`}
              css={css`
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 4px;
                border: none;
                padding: 4px;
                cursor: pointer;
                background: transparent;
                transition: all 0.2s ease;

                &:hover {
                  transform: scale(1.1);
                }

                &:focus {
                  outline: none;
                }
              `}
            >
              {/* Label */}
              <span
                css={css`
                  ${fontStyles.mouse};
                  color: ${isActive ? colors.petrolBlue : colors.tungsten};
                  opacity: ${isActive ? 1 : 0.5};
                  transition: all 0.2s ease;
                  font-weight: 500;

                  html[data-theme='dark'] & {
                    color: ${isActive ? colors.skyBlue : colors.silver};
                  }
                `}
              >
                {feature.icon}
              </span>

              {/* Progress bar under label */}
              <div
                css={css`
                  width: 100%;
                  height: 3px;
                  border-radius: 2px;
                  background: ${colors.cloud};
                  overflow: hidden;
                  transition: background 0.2s ease;

                  html[data-theme='dark'] & {
                    background: ${colors.tungsten};
                  }
                `}
              >
                {isActive && (
                  <div
                    css={css`
                      height: 100%;
                      width: ${progress}%;
                      background: ${colors.petrolBlue};
                      border-radius: 2px;
                      transition: width 50ms linear;

                      html[data-theme='dark'] & {
                        background: ${colors.skyBlue};
                      }
                    `}
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
